import { useMemo } from 'react';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  CONTRIBUTOR_ROLE_NAV_MODULE_KEY,
  resolveContributorShellState,
  getContributorRoleLabel,
  getContributorNavProfile,
} from '../../lib/contributorExperience';
import { buildRoleMenu, type RoleMenu } from '../../lib/contributorNavProfiles';
import { useModuleGate } from '../../lib/moduleGate';
import { useAccessibleApps } from '../../apps/RequireAppAccess';
import { useSpaceKind } from './useSpaceKind';
import { useTimePhasedPlanningEnabled } from '../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';

export function useContributorExperience() {
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const currentRole = useUserStore((s) => s.currentRole);
  const roleNavEnabled = useModuleGate(CONTRIBUTOR_ROLE_NAV_MODULE_KEY);

  const roleNames = assignedRoleNames ?? [];
  const { useContributorShell, shellReady } = resolveContributorShellState(
    permissionsLoaded,
    currentRole,
    roleNames,
    roleNavEnabled,
  );

  const roleLabel = getContributorRoleLabel(roleNames);
  const profile = getContributorNavProfile(roleNames);

  return {
    useContributorShell,
    shellReady,
    roleLabel,
    navProfile: profile,
    defaultPath: profile.defaultPath,
    assignedRoleNames: roleNames,
  };
}

/**
 * The role-based menu for the signed-in person — the one list both the desktop
 * short side menu and the phone drawer draw (see `buildRoleMenu`). Pass
 * 'plans' for the Plans side menu (PlanningSidebar, the phone's Plans drawer).
 */
export function useRoleMenu(product: 'priorities' | 'plans' = 'priorities'): RoleMenu {
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const featuredJobRoleSlugs = useUserStore((s) => s.featuredJobRoleSlugs);
  const canAny = useUserStore((s) => s.canAny);
  // Re-run the permission checks once permissions arrive or change.
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const resolvedCapabilityIds = useUserStore((s) => s.resolvedCapabilityIds);
  const currentScope = useUserStore((s) => s.currentScope);
  const currentRole = useUserStore((s) => s.currentRole);
  const { apps } = useAccessibleApps();
  const spaceKind = useSpaceKind();
  const hasApps = apps.length > 0;
  // Same rule as the full menu: Plans and the Plan of Record are not offered in a personal space.
  const plansAvailable = spaceKind !== 'personal';
  const launchPlanningAvailable = useTimePhasedPlanningEnabled();

  return useMemo(
    () => buildRoleMenu({
      assignedRoleNames: assignedRoleNames ?? [],
      featuredJobRoleSlugs: featuredJobRoleSlugs ?? [],
      canAny,
      hasApps,
      plansAvailable,
      product,
      launchPlanningAvailable,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product, launchPlanningAvailable, assignedRoleNames, featuredJobRoleSlugs, canAny, hasApps, plansAvailable, permissionsLoaded, resolvedCapabilityIds, currentScope, currentRole],
  );
}
